/**
 * ProxyInferenceClient (RFC 0217: reify the run).
 *
 * A seam IMPLEMENTATION of the generic InferenceClient contract (defined in
 * src/workers/seams.ts by RFC 0216). It binds the existing mod-llm-proxy client
 * (llmClient.chatStream) to the worker's seam. It lives beside llmClient, NOT in
 * src/workers/: the worker infrastructure stays provider-agnostic, and a
 * different inference backend is just a different InferenceClient implementation,
 * with zero change to the worker module. mod-llm-proxy itself is unchanged.
 *
 * Chunk folding: llmClient.chatStream already strips `mod_usage` (-> onUsageUpdate)
 * and throws on `error`, so this adapter only sees content chunks. The proxy emits
 * a consolidated `tool_use` chunk ({id,name,input}); we also assemble the
 * `input_json_delta` path defensively in case a backend streams tool arguments.
 */

import type { TranscriptMessage } from '../../log/projection-transcript'
import type { ModelRef, ModelSelection } from '../../log/types'
import type {
  InferenceChunk,
  InferenceClient,
  InferenceStreamOpts,
  ModelOption,
} from '../../workers/seams'
import type { ChatRequest, LLMClient, LLMProvider, Tool } from './llmClient'
import type { ContentBlockDelta, StreamChunk, ToolUseChunk } from './sseParser'
import {
  type ResolvedAttachments,
  toChatMessages,
  withCurrentViewOnly,
} from './to-chat-messages'
import { normalizeToolArgs } from './tool-args'

export interface ProxyInferenceContext {
  /** Project/thread tags forwarded to the proxy for auth/billing scoping. */
  workspaceId?: string
  threadId?: string
  /** Tools advertised to the model (existing proxy Tool[] shape). */
  getTools?: () => Tool[] | undefined
  /** Fallback provider/model when a run carries none. */
  defaultProvider?: LLMProvider
  defaultModel?: string
  system?: string
  /** The user-selected model id (read lazily), applied when a run carries none.
   *  Wins over `defaultModel`, loses to a run's addressed model. */
  getModel?: () => string | undefined
  /**
   * Fetch an attachment document's bytes as base64 (RFC 0288).
   *
   * A SEAM rather than a repo, because this module has no repo and every host
   * reaches its documents differently. Absent (or returning null) means the
   * transcript still mentions the file by name; only the picture is missing.
   */
  resolveAttachment?: (
    docId: string,
  ) => Promise<{ mediaType: string; base64: string } | null>
}

/** The models the proxy offers in its picker (starter set; the proxy can route
 *  more, but these are the selectable defaults). */
const PROXY_MODELS: ModelOption[] = [
  { id: 'gpt-5.2', description: 'OpenAI (default)' },
  { id: 'gpt-5.5', description: 'OpenAI' },
]

/** A composer model choice with everything a picker needs and everything a run
 *  needs, in one shape (RFC 0262). `id` keys the selection; `label` is shown;
 *  `provider`/`model` become the run's `requestedModel`. Mirrors iOS's
 *  FFI `ModelOption`, so the web and native pickers read the same catalog. */
export interface ComposerModel {
  id: string
  label: string
  provider: string
  model: string
}

/** The selectable models for the web composer, provider-explicit so a selection
 *  writes a conformant `requestedModel`. Same catalog the proxy resolves. */
export const COMPOSER_MODELS: ComposerModel[] = [
  { id: 'gpt-5.2', label: 'GPT-5.2', provider: 'openai', model: 'gpt-5.2' },
  { id: 'gpt-5.5', label: 'GPT-5.5', provider: 'openai', model: 'gpt-5.5' },
]

function asModelRef(model: unknown): Partial<ModelRef> {
  return model && typeof model === 'object' ? (model as Partial<ModelRef>) : {}
}

/**
 * RFC 0262 — unwrap a value that may be an Automerge `ImmutableString`.
 *
 * A run's `requestedModel` read back off the log is NOT a JS string: Automerge
 * hands back an `ImmutableString` wrapper (the real value sits on `.val`).
 * Passing that straight through would write `resolvedModel: { provider: {…} }`
 * onto the step — a wrapped object where whiffletree requires a transparent
 * scalar string, i.e. a non-conformant entry on the explicit-model path.
 * Returns undefined for anything that is not string-like, so the caller's
 * `??` fallback chain still applies.
 */
function plainString(value: unknown): string | undefined {
  if (typeof value === 'string') return value
  if (value == null || typeof value !== 'object') return undefined
  const val = (value as { val?: unknown }).val
  return typeof val === 'string' ? val : undefined
}

export class ProxyInferenceClient implements InferenceClient {
  readonly toolExecution = 'worker' as const

  constructor(
    private readonly llm: LLMClient,
    private readonly ctx: ProxyInferenceContext = {},
  ) {}

  models(): ModelOption[] {
    return PROXY_MODELS
  }

  /**
   * RFC 0262 — the single source of truth for "which model does this call
   * actually run". `stream` builds its request from this, and the inference
   * worker records it as whiffletree's REQUIRED `inference_step.resolvedModel`,
   * so the recorded value is exactly the one used — never a placeholder.
   *
   * `provider` is returned as a transparent string (whiffletree's on-doc shape);
   * the `LLMProvider` narrowing is a wire-request concern, applied in `stream`.
   * Values are unwrapped via {@link plainString}: a run's `requestedModel` read
   * back off the log arrives as Automerge `ImmutableString`, and returning that
   * verbatim would record a wrapped object instead of a scalar string.
   */
  resolveModel(opts: { model?: ModelRef | unknown }): ModelSelection {
    const model = asModelRef(opts.model)
    return {
      provider:
        plainString(model.provider) ?? this.ctx.defaultProvider ?? 'anthropic',
      // Accept both mod's runtime ModelRef (`.id`) and whiffletree's aligned
      // on-doc ModelSelection (`.model`, from a run's requestedModel).
      model:
        plainString(model.id) ??
        plainString((model as { model?: unknown }).model) ??
        this.ctx.getModel?.() ??
        this.ctx.defaultModel ??
        'claude-sonnet-4-20250514',
    }
  }

  /**
   * Fetch the bytes of every IMAGE attachment in the transcript (RFC 0288).
   *
   * Images only: a PDF or a zip has no representation the model can read, so
   * inlining it would spend the context window on base64 nothing will look at.
   * Those still reach the model as a named mention from `blocksOf`.
   *
   * Deduplicated by document id, because the same image referenced twice in a
   * long thread is the same bytes, and fetched concurrently so a message with
   * several images costs one round trip's latency rather than N.
   */
  private async resolveAttachments(
    input: TranscriptMessage[],
  ): Promise<ResolvedAttachments | undefined> {
    const resolve = this.ctx.resolveAttachment
    if (!resolve) return undefined
    const ids = new Set<string>()
    for (const m of input) {
      for (const b of m.content) {
        if (
          (b.type === 'attachment' || b.type === 'view_attachment') &&
          b.mimeType.startsWith('image/')
        ) {
          ids.add(b.docId)
        }
      }
    }
    if (ids.size === 0) return undefined

    const out: ResolvedAttachments = new Map()
    await Promise.all(
      Array.from(ids).map(async (id) => {
        try {
          const hit = await resolve(id)
          if (hit) out.set(id, hit)
        } catch {
          // An attachment that will not load must not fail the whole turn: the
          // message still goes, naming the file it could not open.
        }
      }),
    )
    return out
  }

  async *stream(
    input: TranscriptMessage[],
    opts: InferenceStreamOpts,
  ): AsyncIterable<InferenceChunk> {
    const resolved = this.resolveModel(opts)
    const turn = withCurrentViewOnly(input)
    const req: ChatRequest = {
      provider: resolved.provider as LLMProvider,
      model: resolved.model,
      messages: toChatMessages(turn, await this.resolveAttachments(turn)),
      tools: this.ctx.getTools?.(),
      // The turn's own text (RFC 0334 3c) after the standing prompt.
      system:
        [this.ctx.system, opts.system].filter(Boolean).join('\n\n') ||
        undefined,
      stream: true,
      workspace_id: this.ctx.workspaceId,
      thread_id: this.ctx.threadId,
    }

    // Per content-block index: accumulate streamed tool-argument JSON, plus the
    // block's id/name if they arrived via content_block_start.
    const pending = new Map<
      number,
      { id?: string; name?: string; json: string }
    >()

    for await (const chunk of this.llm.chatStream(req, opts.admission)) {
      const c = chunk as StreamChunk
      switch (c.type) {
        case 'tool_use': {
          const t = c as unknown as ToolUseChunk & { arguments?: unknown }
          yield {
            toolUse: {
              id: t.id,
              name: t.name,
              // Backends disagree: object `input`, JSON-string `input`, or a
              // JSON-string `arguments` (OpenAI). Normalize to a plain object.
              arguments: normalizeToolArgs(t.input ?? t.arguments),
            },
          }
          break
        }
        case 'content_block_start': {
          const cb = c as unknown as {
            index: number
            content_block?: { type?: string; id?: string; name?: string }
          }
          if (cb.content_block?.type === 'tool_use') {
            pending.set(cb.index, {
              id: cb.content_block.id,
              name: cb.content_block.name,
              json: '',
            })
          }
          break
        }
        case 'content_block_delta': {
          const d = c as unknown as ContentBlockDelta
          if (d.delta.type === 'text_delta' && d.delta.text) {
            yield { text: d.delta.text }
          } else if (d.delta.type === 'input_json_delta') {
            const slot = pending.get(d.index)
            if (slot) slot.json += d.delta.partial_json ?? ''
          }
          break
        }
        case 'content_block_stop': {
          const idx = (c as unknown as { index: number }).index
          const slot = pending.get(idx)
          if (slot?.id && slot.name) {
            yield {
              toolUse: {
                id: slot.id,
                name: slot.name,
                arguments: normalizeToolArgs(slot.json),
              },
            }
          }
          pending.delete(idx)
          break
        }
        case 'message_delta': {
          const stop = (c as unknown as { delta?: { stop_reason?: string } })
            .delta?.stop_reason
          if (stop != null) yield { stopReason: stop }
          break
        }
        // message_start / message_stop / content_block_start(text): no worker chunk.
      }
    }
  }
}
